import React, { useState, useCallback, useRef } from 'react';
import { useNavigate} from 'react-router-dom';
import { Accordion, Span, Alert, Heading, Stack, Box, SegmentGroup, HStack, Text, Textarea, Field,Flex, Spacer, Input, Select, createListCollection, Portal, Button} from '@chakra-ui/react';
import axios from 'axios';
import { LuFolderCheck as LuIngest, LuSquareCheck, LuUser, LuSearch, LuTextSearch as LuSearchResults} from "react-icons/lu"
import PageTitle from 'components/ui/PageTitle';
import PageContent from 'components/ui/PageContent';
import SampleSelect from 'components/ui/SampleSelect';

function SearchPage() {
  const navigate = useNavigate();
  
  // Create refs for form inputs instead of state
  const sequenceRef = useRef("");
  const titleRef = useRef("");
  const programRef = useRef("blastn");
  const maxHitsRef = useRef("200");
  const percIdentityRef = useRef("");
  const evalueRef = useRef("");
  const wordSizeRef = useRef("");
  const gapopenRef = useRef("");
  const gapextendRef = useRef("");
  
  // Keep this as state since it's set by the SampleSelect component
  const [blastReqSampleIds, setBlastReqSampleIds] = useState([]);
  const [result, setResult] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);

  const programChangeHandler = useCallback((e) => {
    programRef.current = e.value;
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult("Pending...");
    try {
      // Get values from refs when submitting
      const sequence = sequenceRef.current.value;
      const program = programRef.current;
      const title = titleRef.current.value;
      const maxHits = maxHitsRef.current.value;
      const percIdentity = percIdentityRef.current.value;
      const evalue = evalueRef.current.value;
      const wordSize = wordSizeRef.current.value;
      const gapopen = gapopenRef.current.value;
      const gapextend = gapextendRef.current.value;
      
      // validate input
      if (!sequence) {
        setResult('Error: Query sequence is empty');
        return;
      }
      if (!blastReqSampleIds || blastReqSampleIds.length === 0) {
        setResult('Error: No samples to search against, please select at least one sample');
        return;
      }

      // Add FASTA header before sending to backend
      const fastaSequence = `>query\n${sequence}`;
      const response = await axios.post('/api/search', {
        sequence: fastaSequence,
        blast_program: program,
        description: title,
        blast_dbs: blastReqSampleIds,
        // jsonify the options
        blast_options: JSON.stringify({
          max_hits: maxHits,
          perc_identity: percIdentity,
          evalue: evalue,
          word_size: wordSize,
          gapopen: gapopen,
          gapextend: gapextend,
        }),
      });
      setResult(response.data.result);

      // go to search results page
      // navigate when response is successful
      if (response.status === 200) {
        navigate('/search_results');
      }
    } catch (error) {
      console.error('Error submitting BLAST job', error);
      setResult('Error submitting BLAST job: '+ error.response.data.detail);
    }
  };

  return (
    <PageContent>
      <PageTitle icon={LuSearch} title="BLAST Search" />

      <Flex>
        <SegmentGroup.Root 
          defaultValue='blastn'
          onValueChange={programChangeHandler}
          colorPalette="brand"
          size="sm"
          css={{ bg: 'gray.50'}}
        >
          <SegmentGroup.Indicator css={{ bg: 'brand.muted'}}  />
          <SegmentGroup.Items items={["blastn", "blastp", "blastx", "tblastn", "tblastx"]} />
        </SegmentGroup.Root>
      </Flex>

      <Field.Root required>
        <Field.Label>
          Enter Query Sequence <Field.RequiredIndicator />
        </Field.Label>
        <Textarea 
          placeholder="Sequence..." 
          ref={sequenceRef}
        />
      </Field.Root>

      <Field.Root>
        <Field.Label>
          Job Title
        </Field.Label>
        <Input 
          size="sm" 
          placeholder='Job Title'
          ref={titleRef}
        /> 
      </Field.Root>

      <Accordion.Root collapsible variant="plain" defaultValue={["val"]}>
        <Accordion.Item key="key" value="val">
          <Accordion.ItemTrigger _hover={{cursor: 'pointer'}}>
            <Span><Heading>Advanced Options</Heading></Span>
            <Accordion.ItemIndicator />
          </Accordion.ItemTrigger>
          <Accordion.ItemContent>
            <Accordion.ItemBody>
              <Field.Root maxW="15em">
                <HStack>
                  <Field.Label flex="0 0 auto" minW="10em">
                    Maximum Hits
                  </Field.Label>
                  <Input 
                    size="sm"
                    defaultValue="200"
                    ref={maxHitsRef}
                  /> 
                </HStack>
              </Field.Root>

              <Field.Root maxW="15em">
                <HStack>
                  <Field.Label flex="0 0 auto" minW="10em">
                    Min % Identity
                  </Field.Label>
                  <Input 
                    size="sm" 
                    placeholder='0'
                    ref={percIdentityRef}
                  /> 
                </HStack>
              </Field.Root>

              <Field.Root maxW="15em">
                <HStack>
                  <Field.Label flex="0 0 auto" minW="10em">
                    Max Expect Value
                  </Field.Label>
                  <Input 
                    size="sm" 
                    placeholder='10'
                    ref={evalueRef}
                  /> 
                </HStack>
              </Field.Root>

              <Field.Root maxW="15em">
                <HStack>
                  <Field.Label flex="0 0 auto" minW="10em">
                    Word Size
                  </Field.Label>
                  <Input 
                    size="sm" 
                    placeholder='Default'
                    ref={wordSizeRef}
                  /> 
                </HStack>
              </Field.Root>

              <Field.Root maxW="15em">
                <HStack>
                  <Field.Label flex="0 0 auto" minW="10em">
                    Gap Open Cost
                  </Field.Label>
                  <Input 
                    size="sm" 
                    placeholder='Default'
                    ref={gapopenRef}
                  /> 
                </HStack>
              </Field.Root>

              <Field.Root maxW="15em">
                <HStack>
                  <Field.Label flex="0 0 auto" minW="10em">
                    Gap Extension Cost
                  </Field.Label>
                  <Input 
                    size="sm" 
                    placeholder='Default'
                    ref={gapextendRef}
                  /> 
                </HStack>
              </Field.Root>
            </Accordion.ItemBody>
          </Accordion.ItemContent>
        </Accordion.Item>
      </Accordion.Root>

      <Heading>Choose Search Set</Heading>
      <Flex>
        <SampleSelect onChange={(sample_ids) => {
          console.log('sample_ids:', sample_ids);
          setBlastReqSampleIds(sample_ids);
        }} />
      </Flex>
      
      <HStack>
        <Button size="lg" onClick={handleSubmit}>
          Start BLAST on {blastReqSampleIds.length} Sample{blastReqSampleIds.length === 1 ? '': 's' }
        </Button>

        {result && (
          <Alert.Root status={result.startsWith("Pending") ? "info" : (result.startsWith('Error') ? 'error' : 'success')}>  
            <Alert.Indicator />
            <Alert.Title>{result}</Alert.Title>
          </Alert.Root>
        )}
      </HStack>
    </PageContent>
  );
}

export default SearchPage;